import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock } from "lucide-react";

const IDLE_MS = 30 * 60 * 1000;   // 30 minutos sem uso
const WARN_MS = 2 * 60 * 1000;    // aviso 2 minutos antes
const PING_MS = 2 * 60 * 1000;    // avisa o servidor que ainda está em uso

/** Desconecta após 30 min sem uso (toque, clique, teclado, rolagem ou vídeo em reprodução). */
export const IdleWatcher = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const last = useRef(Date.now());
  const lastPing = useRef(Date.now());
  const [left, setLeft] = useState(null); // ms até desconectar (quando no aviso)

  useEffect(() => {
    if (!user) return;
    last.current = Date.now();
    const mark = () => { last.current = Date.now(); };
    const evs = ["pointerdown", "keydown", "wheel", "touchstart", "scroll", "mousemove"];
    let mm = 0;
    const onMove = () => { const n = Date.now(); if (n - mm > 5000) { mm = n; mark(); } };
    evs.forEach((e) => window.addEventListener(e, e === "mousemove" ? onMove : mark, { passive: true }));
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);

    const signOut = async () => {
      try { sessionStorage.setItem("iep-session-msg", "Sua sessão foi encerrada por inatividade (30 minutos sem uso). Entre novamente."); } catch (e) {}
      setLeft(null);
      await logout();
      navigate("/login", { replace: true });
    };
    const check = () => {
      // vídeo/embed em foco (a pessoa clicou no player) conta como uso
      if (document.visibilityState === "visible" && document.activeElement?.tagName === "IFRAME") mark();
      const idle = Date.now() - last.current;
      if (idle >= IDLE_MS) return signOut();
      if (idle >= IDLE_MS - WARN_MS) setLeft(IDLE_MS - idle); else setLeft(null);
      if (idle < PING_MS && Date.now() - lastPing.current > PING_MS) {
        lastPing.current = Date.now();
        api.post("/auth/ping").catch(() => {});
      }
    };
    const t = setInterval(check, 1000);
    return () => {
      clearInterval(t);
      evs.forEach((e) => window.removeEventListener(e, e === "mousemove" ? onMove : mark));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user || left === null) return null;
  const s = Math.ceil(left / 1000);
  const stay = () => { last.current = Date.now(); lastPing.current = Date.now(); setLeft(null); api.post("/auth/ping").catch(() => {}); };
  return (
    <Dialog open onOpenChange={(o) => !o && stay()}>
      <DialogContent className="max-w-sm" data-testid="idle-warning">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" /> Você ainda está aí?</DialogTitle>
          <DialogDescription>Por segurança, você será desconectado(a) por inatividade em <strong>{Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}</strong>.</DialogDescription>
        </DialogHeader>
        <Button onClick={stay} className="w-full text-white h-11" style={{ background: "#1E9E2A" }} data-testid="idle-stay">Continuar conectado</Button>
      </DialogContent>
    </Dialog>
  );
};
